import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Sparkles, ShieldAlert, Coins } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

export default function StreakRecoveryCard({ onRecovered }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/streak/status").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const { tokens, pre_break_streak, can_recover, recovery_required, recovery_progress, recovery_ready } = data;
  const pct = Math.min(100, (recovery_progress / recovery_required) * 100);

  // Variant 1: Streak broken, recovery available
  if (can_recover) {
    const recover = async () => {
      setBusy(true);
      try {
        const r = await api.post("/streak/recover");
        toast.success(t("streakRecoveryCard.streakRestoredToDaysMashallah", { streak: r.data.streak }));
        load();
        onRecovered?.(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("streakRecoveryCard.couldnTRecoverStreak"));
      } finally { setBusy(false); }
    };
    return (
      <div className="hifz-card border-terracotta-500/30 bg-terracotta-500/5" data-testid="streak-recovery-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-terracotta-500/15 text-terracotta-600 grid place-items-center">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.2em] text-terracotta-600 font-semibold">{t("streakRecoveryCard.streakBreakDetected")}</div>
            <h3 className="mt-1 font-display font-semibold text-lg text-forest-700">
              {t("streakRecoveryCard.restoreYourDayStreak", { pre_break_streak })}
            </h3>
            <p className="mt-1 text-sm text-foreground/75">
              {t("streakRecoveryCard.completeAyahsTodayAndSpend", { recovery_required })}
            </p>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t("streakRecoveryCard.todaySRecoveryProgress")}</span>
            <span className="font-display font-semibold text-forest-700">{recovery_progress} / {recovery_required}</span>
          </div>
          <div className="mt-2 h-2 bg-white rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-terracotta-500 to-gold-500 transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 text-sm" data-testid="recovery-tokens">
            <Coins className="h-4 w-4 text-gold-600" />
            <span className="text-foreground/80">{t("streakRecoveryCard.hifzTokensThisWeek", { count: tokens })}</span>
          </div>
          <button
            onClick={recover}
            disabled={!recovery_ready || busy}
            data-testid="recover-streak-btn"
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <Flame className="h-4 w-4" />
            {busy ? t("streakRecoveryCard.restoring") : recovery_ready ? t("streakRecoveryCard.recoverStreak") : t("streakRecoveryCard.keepGoing")}
          </button>
        </div>
      </div>
    );
  }

  // Variant 2: Default — show token in idle state
  return (
    <div className="hifz-card" data-testid="streak-recovery-idle">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-gold-500/15 text-gold-600 grid place-items-center">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] text-gold-600 font-semibold">{t("streakRecoveryCard.hifzTokens")}</div>
          <h3 className="mt-1 font-display font-semibold text-lg text-forest-700">{t("streakRecoveryCard.tokensThisWeek", { count: tokens })}</h3>
          <p className="mt-1 text-sm text-foreground/75">
            {t("streakRecoveryCard.tokensRefillEveryMondayIf")}
          </p>
        </div>
      </div>
    </div>
  );
}
